'use client';

import { createContext, useCallback, useContext, useMemo } from 'react';
import { configureAuth } from './configure';
import { clearTokens, fetchSession, signOutAndClearTokens } from './session';
import type { AuthContextValue, AuthProviderProps, CreateAuthOptions } from './types';

/**
 * Sets up auth for an app and returns its typed provider and hook. Call once,
 * at module load: it configures Amplify immediately, so anything rendered
 * later (an Authenticator, a query) finds it ready.
 */
export const createAuth = <TSession, THeaders>({
  config,
  extractSession,
  extractHeaders,
}: CreateAuthOptions<TSession, THeaders>) => {
  configureAuth(config);

  const Context = createContext<AuthContextValue<TSession, THeaders> | null>(null);

  const getSession = async () => extractSession(await fetchSession());
  const getSessionHeaders = async () => extractHeaders(await getSession());

  const AuthProvider = ({ onSignOut, children }: AuthProviderProps) => {
    const clearSession = useCallback(async () => {
      try {
        await signOutAndClearTokens();
      } finally {
        onSignOut?.();
      }
    }, [onSignOut]);

    const value = useMemo(
      () => ({ clearSession, clearTokens, getSession, getSessionHeaders }),
      [clearSession]
    );

    return <Context.Provider value={value}>{children}</Context.Provider>;
  };

  const useAuthContext = () => {
    const ctx = useContext(Context);
    if (!ctx) throw new Error('useAuthContext must be used within <AuthProvider>');
    return ctx;
  };

  return { AuthProvider, useAuthContext };
};
